import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { processCSS } from '../../utils/codeConverters';

/**
 * ComponentPreview
 * Renders the provided HTML, CSS, and JS inside an isolated iframe
 * so styles don’t leak between previews and render exactly as authored.
 */
const ComponentPreview = ({ htmlCode, cssCode, jsCode }) => {
  const iframeRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;

    try {
      setError(null);
      const safeCSS = processCSS(cssCode || '');
      const html = htmlCode || '<div style="color:#6b7280;font-size:14px;">No content to preview</div>';
      const js = jsCode || '';

      const page = `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      html, body { height: 100%; margin: 0; }
      body { display:flex; justify-content:center; align-items:center; padding: 16px; font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif; color:#111827; }
      img{ max-width:100%; height:auto; display:block; }
      ${safeCSS}
    </style>
  </head>
  <body>
    <div id="root">${html}</div>
    <script>
      // Make preview passive: prevent submissions and state changes but keep hover effects
      (function() {
        // Prevent all form submissions at capture phase
        document.addEventListener('submit', function(e) {
          e.preventDefault();
          e.stopPropagation();
        }, true);

        // Helpers to detect allowed toggle controls
        function isAllowedToggle(node) {
          if (!node || !node.matches) return false;
          if (node.matches('input[type="checkbox"], input[type="radio"], [role="switch"], [data-preview-allow]')) return true;
          return false;
        }
        function labelTargetsToggle(label) {
          if (!label) return false;
          const forId = label.getAttribute('for') || label.htmlFor;
          if (forId) {
            const target = document.getElementById(forId);
            return isAllowedToggle(target);
          }
          // label wrapping input
          const inside = label.querySelector('input[type="checkbox"], input[type="radio"], [role="switch"]');
          return !!inside;
        }

        // Prevent clicks that would trigger navigation or state changes, but allow toggles
        document.addEventListener('click', function(e) {
          const el = e.target && (e.target.closest && e.target.closest('a, button, input, select, textarea, label'));
          if (!el) return;
          // Allow checkbox/radio/switch and labels that target them
          if (isAllowedToggle(el) || (el.tagName === 'LABEL' && labelTargetsToggle(el))) {
            return; // allow
          }
          e.preventDefault();
          e.stopPropagation();
        }, true);

        // Avoid focusing interactive elements (prevents typing while preserving hover styles)
        document.addEventListener('focusin', function(e) {
          const t = e.target;
          if (!t || !t.matches) return;
          if (t.matches('a, button, input, select, textarea')) {
            // Allow focus for toggles
            if (isAllowedToggle(t)) return;
            e.preventDefault();
            e.stopPropagation();
            if (typeof t.blur === 'function') {
              try { t.blur(); } catch(_) {}
            }
          }
        }, true);

        // Make text inputs readOnly so they look normal but don't accept typing
        function applyReadOnly() {
          document.querySelectorAll('input, textarea').forEach(el => {
            const type = (el.getAttribute('type') || '').toLowerCase();
            if (!['checkbox','radio','range','file','color','date','datetime-local','month','time','week'].includes(type)) {
              el.setAttribute('readonly', 'readonly');
            }
          });
        }
        applyReadOnly();
        document.addEventListener('DOMContentLoaded', applyReadOnly);

        // Block Enter/Space from triggering buttons/links while allowing other keys
        document.addEventListener('keydown', function(e) {
          const key = e.key;
          const target = e.target;
          if (!target || !target.matches) return;
          const isInteractive = target.matches('a, button, input, select, textarea');
          if (isInteractive && (key === 'Enter' || key === ' ')) {
            // Allow keyboard toggling for toggles
            if (isAllowedToggle(target)) return;
            e.preventDefault();
            e.stopPropagation();
          }
        }, true);
      })();
      // User JS
      (function(){
        try { ${js} } catch (e) { console.error(e); }
      })();
    </script>
  </body>
</html>`;

      doc.open();
      doc.write(page);
      doc.close();
    } catch (e) {
      console.error('Preview render error:', e);
      setError(String(e?.message || e));
    }
  }, [htmlCode, cssCode, jsCode]);

  return (
    <div className="relative w-full h-64 rounded-xl overflow-hidden bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      <iframe
        ref={iframeRef}
        title="component-preview"
        className="w-full h-full"
        style={{ border: 'none', background: 'transparent' }}
      />
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-red-50/90 dark:bg-red-900/20 backdrop-blur-sm p-4">
          <div className="max-w-md p-4 text-sm text-center text-red-600 dark:text-red-400 bg-white/90 dark:bg-black/90 rounded-lg shadow-lg">
            <svg className="w-6 h-6 mx-auto mb-2 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="font-medium mb-1">Preview Error</div>
            <div>{error}</div>
          </div>
        </div>
      )}
    </div>
  );
};

ComponentPreview.propTypes = {
  htmlCode: PropTypes.string,
  cssCode: PropTypes.string,
  jsCode: PropTypes.string,
};

export default ComponentPreview;